import React, { useState, forwardRef } from 'react'
import { MatxProgressBar } from 'app/components'
import {
  Fab,
  Icon,
  Card,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  FormHelperText,
} from '@mui/material'
import { toast } from 'react-toastify'
import makeStyles from '@mui/styles/makeStyles';
import { useImperativeHandle } from 'react'

const useStyles = makeStyles(({ palette, ...theme }) => ({
  dropzone: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
}))

const FilesPicker = (
  {
    initialValue = [],
    handleChange,
    isSubmitting,
    error,
    pdbFilesOnly,
    isReadOnly,
  },
  ref
) => {
  const classes = useStyles()
  const [files, setFiles] = useState(initialValue)
  //   const [dargClass, setDragClass] = useState('')

  useImperativeHandle(
    ref,
    () => ({
      clearFiles: () => setFiles([]),
    }),
    []
  )

  const isCsvFile = (name) => {
    return name && name.endsWith('.csv')
  }
  const isPdbFile = (name) => {
    return name && name.endsWith('.pdb')
  }
  const isZipFile = (name) => {
    return name && name.endsWith('.zip')
  }
  const isFilesValid = (files) => {
    if (files.length === 0) return true
    if (pdbFilesOnly) {
      for (const file of files) {
        if (!isPdbFile(file.name)) {
          toast.error('You can choose .pdb files only')
          return false
        }
      }
    } else {
      if (files.length === 1) {
        if (!isCsvFile(files[0].name) && !isZipFile(files[0].name)) {
          toast.error('You can choose .csv or .zip files only')
          return false
        }
      } else {
        for (const file of files) {
          if (!isCsvFile(file.name)) {
            toast.error('You can choose .csv files only')
            return false
          }
        }
      }
    }

    return true
  }

  const handleFileSelect = (event) => {
    let _files = event.target.files
    if (!isFilesValid(_files)) return
    let list = []

    const names = new Set(files.map(({ file }) => file.name))
    for (const iterator of _files) {
      const fileName = iterator.name
      if (names.has(fileName)) {
        toast.error(`${fileName} is already selected`)
      } else {
        list.push({
          file: iterator,
          uploading: false,
          error: false,
          progress: 0,
          updateView: () => setFiles((prev) => [...prev]),
        })
      }
    }
    setFiles([...files, ...list])
    handleChange([...files, ...list])
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    // setDragClass('drag-shadow')
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.persist()

    let _files = event.dataTransfer.files
    if (!isFilesValid(_files)) return
    let list = []

    const names = new Set(files.map(({ file }) => file.name))
    for (const iterator of _files) {
      const fileName = iterator.name
      if (names.has(fileName)) {
        toast.error(`${fileName} is already selected`)
      } else {
        list.push({
          file: iterator,
          uploading: false,
          error: false,
          progress: 0,
          updateView: () => setFiles((prev) => [...prev]),
        })
      }
    }

    // setDragClass('')
    setFiles([...files, ...list])
    handleChange([...files, ...list])

    return false
  }

  const handleDragStart = (event) => {
    // setDragClass('drag-shadow')
  }

  const handleSingleRemove = (index) => {
    if (isSubmitting) return
    let tempList = [...files]
    tempList.splice(index, 1)
    setFiles([...tempList])
    handleChange(tempList)
  }

  let isEmpty = !!!files.length
  return (
    <div style={{ height: '100%' }} className="upload-form relative">
      {!isReadOnly && (
        <div
          className={classes.dropzone}
          onDragEnter={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        />
      )}
      <h5>
        {pdbFilesOnly
          ? 'Drop .pdb files here'
          : 'Drop single .zip file or multiple .csv files here'}
      </h5>
      {error ? (
        <FormHelperText error className="ml-2 mb-4">
          {error}
        </FormHelperText>
      ) : null}
      <div className="flex flex-wrap mb-6">
        <label htmlFor="upload-single-file">
          <Fab
            disabled={isReadOnly}
            className="capitalize text-white px-4"
            color="primary"
            size="small"
            component="span"
            variant="extended"
          >
            <div className="flex items-center">
              <Icon className="pr-8">cloud_upload</Icon>
              <span>Single File</span>
            </div>
          </Fab>
        </label>
        <input
          className="hidden"
          value={[]}
          onChange={handleFileSelect}
          id="upload-single-file"
          type="file"
          accept={pdbFilesOnly ? '.pdb' : '.csv, .zip'}
        />
        <div className="px-2"></div>
        <label htmlFor="upload-multiple-file">
          <Fab
            disabled={isReadOnly}
            className="capitalize text-white px-4"
            color="primary"
            size="small"
            component="span"
            variant="extended"
          >
            <div className="flex items-center">
              <Icon className="pr-8">cloud_upload</Icon>
              <span>Multiple Files</span>
            </div>
          </Fab>
        </label>
        <input
          className="hidden"
          value={[]}
          onChange={handleFileSelect}
          id="upload-multiple-file"
          type="file"
          multiple
          accept={pdbFilesOnly ? '.pdb' : '.csv'}
        />
      </div>

      <Card className="mb-6" elevation={2}>
        <div className="p-4">
          <Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            direction="row"
          >
            <Grid item sm={4}>
              Name
            </Grid>
            <Grid item sm={3}>
              Progress
            </Grid>
            <Grid item sm={2}>
              Status
            </Grid>
            <Grid item sm={3}>
              Actions
            </Grid>
          </Grid>
        </div>
        <Divider></Divider>

        {isEmpty && <p className="px-4">Queue is empty</p>}

        {files.map((item, index) => {
          let { file, uploaded, error, progress } = item
          return (
            <div className="px-4 py-4" key={file.name}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                direction="row"
              >
                <Grid item sm={4}>
                  {file.name}
                </Grid>
                <Grid item sm={3}>
                  <MatxProgressBar value={progress}></MatxProgressBar>
                </Grid>
                <Grid item sm={2}>
                  {error && <Icon color="error">error</Icon>}
                  {uploaded && <Icon className="text-green">done</Icon>}
                </Grid>
                <Grid item sm={3}>
                  <div>
                    <Tooltip title="Remove" placement="top" arrow>
                      <IconButton
                        disabled={isSubmitting || isReadOnly}
                        aria-label="remove"
                        size="small"
                        onClick={() => handleSingleRemove(index)}
                      >
                        <Icon className="text-error">delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </div>
                </Grid>
              </Grid>
            </div>
          );
        })}
      </Card>
    </div>
  );
}

export default forwardRef(FilesPicker)
